import { QueryClient } from '@tanstack/react-query';
import { get, post } from './ApiWrapper';
import useCustomQuery from '../useCustomQuery';
import { staleTimeForSession, staleTimeInOneHour } from '../reactQueryCore';
import { ClientEvent, ClientEventSession, ClientEventSessionNote } from '../../models/ClientEvent';
import useCustomMutation from '../useCustomMutation';

const basePath = 'event';
const eventSessionNoteQueryKey = (eventSessionId: number) => [basePath, 'sessions', eventSessionId, 'note'];

export const useGetClientEvent = () => {
	return useCustomQuery({
		queryKey: [basePath, 'event'],
		queryFn: (): Promise<ClientEvent> => get(`${basePath}`),
		staleTime: staleTimeForSession
	});
};

export const useGetClientEventSessions = (eventId: number) => {
	return useCustomQuery({
		queryKey: [basePath, 'sessions'],
		queryFn: (): Promise<ClientEventSession[]> => get(`${basePath}/${eventId}/sessions`),
		staleTime: staleTimeInOneHour,
		enabled: !!eventId
	});
};

export const useGetClientEventSessionNote = (eventId: number, eventSessionId: number) => {
	return useCustomQuery({
		queryKey: eventSessionNoteQueryKey(eventSessionId),
		queryFn: (): Promise<ClientEventSessionNote> => get(`${basePath}/${eventId}/sessions/${eventSessionId}/note`),
		staleTime: staleTimeForSession
	});
};

export const useAddClientEventSessionNote = (
	queryClient: QueryClient,
	eventId: number,
	eventSessionId: number,
	onSuccess: (note: ClientEventSessionNote) => void
) => {
	return useCustomMutation({
		mutationFn: (note: ClientEventSessionNote) => post(`${basePath}/${eventId}/sessions/${eventSessionId}/note`, note),
		onSuccess: async (data: unknown, variables: ClientEventSessionNote) => {
			await queryClient.invalidateQueries({ queryKey: eventSessionNoteQueryKey(eventSessionId) });
			onSuccess(variables);
		}
	});
};
